import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Facebook, MapPin, Clock, Phone, Mail } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function PublicFooter() {
  const { settings } = useSettings();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const year = new Date().getFullYear();
  const daysShort = t("days_short", { returnObjects: true }) || [];

  return (
    <footer
      className="border-t border-border/60 bg-background mt-24"
      data-testid="public-footer"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="font-display text-2xl tracking-tight mb-3">
            King&rsquo;s <span className="gold-text">Café</span>
          </div>
          <p className="text-sm text-muted-foreground max-w-xs">
            {pickLocalized(settings?.tagline, locale, "")}
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {t("footer.address")}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {pickLocalized(settings?.address, locale, "")}
          </p>
          {settings?.phone && (
            <p className="text-sm text-muted-foreground mt-3 flex items-center gap-1.5">
              <Phone className="w-3 h-3" /> {settings.phone}
            </p>
          )}
          {settings?.email && (
            <a
              href={`mailto:${settings.email}`}
              className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5 hover:text-primary"
            >
              <Mail className="w-3 h-3" /> {settings.email}
            </a>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {t("footer.hours")}
          </div>
          <ul className="text-sm text-muted-foreground space-y-1" data-testid="footer-hours">
            {(settings?.hours || []).map((h, idx) => (
              <li key={idx} className="flex justify-between gap-3">
                <span>{daysShort[h.day] || ""}</span>
                <span>{h.closed ? "—" : `${h.open}–${h.close}`}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">
            {t("footer.follow_us")}
          </div>
          <div className="flex items-center gap-3">
            {settings?.social?.instagram && (
              <a
                href={settings.social.instagram}
                target="_blank"
                rel="noreferrer"
                className="p-2 rounded-full border border-border hover:border-primary/40 hover:text-primary text-muted-foreground"
                data-testid="social-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}
            {settings?.social?.facebook && (
              <a
                href={settings.social.facebook}
                target="_blank"
                rel="noreferrer"
                className="p-2 rounded-full border border-border hover:border-primary/40 hover:text-primary text-muted-foreground"
                data-testid="social-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            )}
          </div>
          <div className="mt-6">
            <LanguageSwitcher testid="lang-switch-footer" />
          </div>
        </div>
      </div>
      <div className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted-foreground">
          <span data-testid="footer-copyright">
            {t("footer.made_with", { year, name: settings?.business_name || "Caffe Bar Kings" })}
          </span>
          <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="footer-legal-nav">
            <Link to="/privacy" className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded" data-testid="footer-legal-privacy">
              {t("legal.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded" data-testid="footer-legal-terms">
              {t("legal.terms")}
            </Link>
            <Link to="/cookies" className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded" data-testid="footer-legal-cookies">
              {t("legal.cookies")}
            </Link>
            <Link to="/imprint" className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded" data-testid="footer-legal-imprint">
              {t("legal.imprint")}
            </Link>
            <span className="text-border" aria-hidden>·</span>
            <Link to="/admin/login" className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded" data-testid="footer-admin-link">
              {t("nav.admin")}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
